import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { authenticatedFetch } from '../../api/apiClient';
import { API_ROUTES } from '../../shared/apiRoutes/apiRoutes';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../utils/constants';
import { readDealerAdminSession } from './dealerAdminSession';
import { readSupportedTenantSession } from './supportedTenantsSession';

const TABS = [
  { id: 'backups', label: 'Backups' },
  { id: 'recovery', label: 'Recovery' },
];

const RECORDED_COVERAGE = {
  auth: {
    liveState: 'unknown',
    detail: 'This service cannot read the Auth backup job, its logs, or its destination.',
    lastRecordedSuccess: {
      origin: 'manual',
      outcome: 'uploaded',
      completedAt: '2026-11-01T17:12:10.857009Z',
      recordedAt: '2026-11-02T01:45:00Z',
    },
    recordedSchedule: {
      state: 'unknown',
      recordedAt: '2026-11-02T01:45:00Z',
      detail: 'The 2 November 08:15 UTC slot was not re-read. The earlier count of zero is not the result of that slot.',
    },
  },
  storage: {
    liveState: 'unknown',
    detail: 'This service cannot read the Storage backup job, its logs, or its destination.',
    lastRecordedSuccess: {
      origin: 'manual',
      outcome: 'complete',
      completedAt: '2026-11-02T00:06:44.778850Z',
      recordedAt: '2026-11-02T00:16:00Z',
    },
    recordedSchedule: {
      state: 'pending',
      recordedAt: '2026-11-02T01:45:00Z',
      detail: 'Not due at the last record. The live schedule was not re-read, so this pending state is historical.',
    },
  },
  alerts: {
    liveState: 'unknown',
    inboxDeliveryConfirmed: false,
    lastRecordedCheckAt: '2026-11-02T01:22:50.312089Z',
    detail: 'The last recorded check was before the 2 November Auth slot and is not the current alert state. Inbox delivery of the synthetic notices was not confirmed.',
  },
};

const RECORDED_RECOVERY = {
  authRecordRecovery: {
    proved: true,
    recordedAt: '2026-11-01T17:35:31Z',
    note: 'Synthetic records matched the account id, Google and Microsoft links, disabled state, and custom claims. Provider sign-in was not restored.',
  },
  storageSoftDelete: {
    proved: true,
    startedAt: '2026-11-01T20:45:31.969Z',
    finishedAt: '2026-11-01T20:45:33.891Z',
    note: 'One synthetic object was soft-deleted and restored. This does not prove recovery of every bucket.',
  },
  storageSnapshotRestore: {
    proved: true,
    startedAt: '2026-11-02T00:11:42.595205Z',
    finishedAt: '2026-11-02T00:13:24.384873Z',
    note: 'An isolated restore of the later snapshot matched bytes and metadata. It does not prove original download URLs.',
  },
};

function formatWhen(value) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString();
}

function formatAge(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return 'Unknown';
  const total = Math.max(0, Number(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours >= 48) return `${Math.floor(hours / 24)} days`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function retentionLabel(value) {
  const match = /^(\d+(?:\.\d+)?)s$/.exec(String(value || ''));
  if (!match) return value || 'Unknown';
  const seconds = Number(match[1]);
  const days = Math.round(seconds / 86400);
  if (days % 7 === 0 && days >= 14) return `${days / 7} weeks`;
  if (days > 0) return `${days} days`;
  return value;
}

export function formatEvidenceDuration(startedAt, finishedAt) {
  const start = Date.parse(startedAt || '');
  const end = Date.parse(finishedAt || '');
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  const total = Math.round((end - start) / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes > 0) return `${minutes} min ${seconds} sec`;
  return `${seconds} sec`;
}

function scheduleItems(live) {
  return Array.isArray(live?.schedules?.items) ? live.schedules.items : null;
}

function scheduleOf(live, kind) {
  const items = scheduleItems(live);
  if (!items) return null;
  return items.find((item) => item.kind === kind) || null;
}

function readyBackups(live) {
  if (!Array.isArray(live?.backups?.items)) return null;
  return live.backups.items.filter((item) => item?.state === 'READY' && item.snapshotTime);
}

function newestReady(live) {
  const listed = readyBackups(live);
  const fromSchedules = (scheduleItems(live) || [])
    .map((item) => item.backup)
    .filter((item) => item?.state === 'READY' && item.snapshotTime);
  const pool = listed == null ? fromSchedules : [...listed, ...fromSchedules];
  if (pool.length === 0) return null;
  return pool.reduce((best, item) => (
    Date.parse(item.snapshotTime) > Date.parse(best.snapshotTime) ? item : best
  ));
}

function sectionMissing(live) {
  if (!live || live.freshness?.stale) return true;
  if (live.database?.error || live.database?.pointInTimeRecoveryEnabled == null || live.database?.deleteProtectionEnabled == null) return true;
  if (!Array.isArray(live.schedules?.items) || live.schedules?.error) return true;
  if (!Array.isArray(live.backups?.items) || live.backups?.error) return true;
  if (live.schedules.items.some((item) => item.evidence === 'unknown')) return true;
  return false;
}

export function summarizeProtection(live) {
  if (!live) {
    return {
      tone: 'unavailable',
      title: 'Status unavailable',
      detail: 'Backup status could not be read.',
    };
  }
  if (live.freshness?.stale) {
    return {
      tone: 'unavailable',
      title: 'Status unavailable',
      detail: 'The last read is stale, so this page will not call the data protected.',
    };
  }
  const schedules = scheduleItems(live);
  const backupsKnown = Array.isArray(live.backups?.items);
  const recent = schedules?.some((item) => item.evidence === 'produced') === true;
  const overdue = (schedules || []).filter((item) => item.evidence === 'overdue');
  const pending = (schedules || []).filter((item) => item.evidence === 'pending');
  const pitr = live.database?.pointInTimeRecoveryEnabled;
  const deletion = live.database?.deleteProtectionEnabled;
  const protectionProblems = [];
  if (pitr === false) protectionProblems.push('Point-in-time recovery is off.');
  if (deletion === false) protectionProblems.push('Delete protection is off.');
  if (schedules && schedules.length === 0) protectionProblems.push('No backup schedule is configured.');

  if (sectionMissing(live)) {
    return {
      tone: 'unavailable',
      title: 'Status unavailable',
      detail: 'Required backup or protection information is missing. Known details are still shown below and are not treated as healthy.',
    };
  }

  if (recent && protectionProblems.length > 0) {
    const extra = overdue.length ? ` ${overdue.map((item) => `${item.kind === 'weekly' ? 'Weekly' : 'Daily'} backup is overdue.`).join(' ')}` : '';
    return {
      tone: 'attention',
      title: 'Backup available — protection needs attention',
      detail: `${protectionProblems.join(' ')}${extra}`,
    };
  }

  if (overdue.length > 0 || protectionProblems.length > 0) {
    const overdueText = overdue.map((item) => `${item.kind === 'weekly' ? 'Weekly' : 'Daily'} backup is overdue.`).join(' ');
    return {
      tone: 'attention',
      title: 'Needs attention',
      detail: `${overdueText} ${protectionProblems.join(' ')}`.trim(),
    };
  }

  if (!recent && pending.length > 0 && pending.length === schedules.length && backupsKnown) {
    return {
      tone: 'pending',
      title: 'First backup pending',
      detail: 'The schedules are new and still inside the grace period. This is not a missed backup.',
    };
  }

  if (recent && pitr === true && deletion === true) {
    return {
      tone: 'protected',
      title: 'Protected',
      detail: 'A recent backup is ready. Point-in-time recovery and delete protection are on.',
    };
  }

  return {
    tone: 'unavailable',
    title: 'Status unavailable',
    detail: 'The backup result is not complete enough to call the data protected.',
  };
}

function toneClass(tone) {
  if (tone === 'protected' || tone === 'passed') return 'border-green-200 bg-green-50 text-green-900';
  if (tone === 'attention') return 'border-amber-200 bg-amber-50 text-amber-950';
  if (tone === 'pending') return 'border-gray-200 bg-white text-gray-900';
  return 'border-gray-200 bg-gray-50 text-gray-800';
}

function valueClass(tone) {
  if (tone === 'protected' || tone === 'passed') return 'text-green-800';
  if (tone === 'attention') return 'text-amber-900';
  return 'text-gray-800';
}

function scheduleLabel(item, known) {
  if (!known) return { text: 'Unknown', tone: 'unavailable' };
  if (!item) return { text: 'Not scheduled', tone: 'attention' };
  if (item.evidence === 'produced') return { text: 'Up to date', tone: 'protected' };
  if (item.evidence === 'pending') return { text: 'First backup pending', tone: 'pending' };
  if (item.evidence === 'overdue') return { text: 'Overdue', tone: 'attention' };
  return { text: 'Unknown', tone: 'unavailable' };
}

function enabledLabel(value) {
  if (value === true) return { text: 'Enabled', tone: 'protected' };
  if (value === false) return { text: 'Disabled', tone: 'attention' };
  return { text: 'Unknown', tone: 'unavailable' };
}

function Indicator({ label, value, tone }) {
  return (
    <div className="min-w-0 rounded border border-gray-200 px-3 py-2">
      <div className="text-sm text-gray-500">{label}</div>
      <div className={`mt-1 text-sm font-semibold ${valueClass(tone)}`}>{value}</div>
    </div>
  );
}

function lastBackupText(live) {
  if (!Array.isArray(live?.backups?.items) && !scheduleItems(live)) return 'Unknown';
  const newest = newestReady(live);
  if (!newest) return Array.isArray(live?.backups?.items) ? 'None yet' : 'Unknown';
  const age = newest.ageSeconds != null
    ? formatAge(newest.ageSeconds)
    : formatAge(Math.max(0, Math.round((Date.parse(live.checkedAt || '') - Date.parse(newest.snapshotTime)) / 1000)));
  const ageText = age === 'Unknown' ? '' : ` (${age} old)`;
  return `${formatWhen(newest.snapshotTime)}${ageText}`;
}

function TechnicalDetails({ live }) {
  const schedules = scheduleItems(live);
  return (
    <details className="rounded border border-gray-200 bg-white" data-testid="backup-technical-details">
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-900">Technical details</summary>
      <div className="space-y-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-700">
        <p>Project: {live.projectId || 'Unknown'}. Database: {live.databaseId || 'Unknown'}. Location: {live.locationId || 'Unknown'}.</p>
        <p>Earliest point-in-time recovery window: {formatWhen(live.database?.earliestVersionTime)}</p>
        {live.database?.error ? <p>{live.database.error.message}</p> : null}
        {live.schedules?.error ? <p>{live.schedules.error.message}{live.schedules.error.role ? ` Required role: ${live.schedules.error.role}.` : ''}</p> : null}
        {Array.isArray(schedules) ? schedules.map((item) => (
          <div key={item.id || item.kind} data-testid={`schedule-${item.kind || 'unknown'}`}>
            <div className="font-medium text-gray-900">{item.kind === 'weekly' ? 'Weekly' : item.kind === 'daily' ? 'Daily' : 'Schedule'}</div>
            <div>Retention: {retentionLabel(item.retention)}</div>
            <div>Created: {formatWhen(item.createTime)}</div>
            {item.weekday ? <div>Weekday: {item.weekday}</div> : null}
            <div>Evidence: {scheduleLabel(item, true).text}</div>
            {item.backup?.expireTime ? <div>Expires: {formatWhen(item.backup.expireTime)}</div> : null}
            {item.explanation ? <p>{item.explanation}</p> : null}
          </div>
        )) : <p>Schedule inventory is unavailable.</p>}
        {live.backups?.error ? <p>{live.backups.error.message}{live.backups.error.role ? ` Required role: ${live.backups.error.role}.` : ''}</p> : null}
        {live.backups?.explanation ? <p>{live.backups.explanation}</p> : null}
        {Array.isArray(live.backups?.items) ? live.backups.items.map((item) => (
          <div key={item.id || item.snapshotTime}>
            {item.state || 'Unknown'} snapshot {formatWhen(item.snapshotTime)}, expires {formatWhen(item.expireTime)}
          </div>
        )) : <p>Backup inventory is unavailable.</p>}
      </div>
    </details>
  );
}

function ResultCard({ title, result, historical = false }) {
  const proved = result?.proved === true;
  const duration = formatEvidenceDuration(result?.startedAt, result?.finishedAt);
  const tone = proved ? 'passed' : 'pending';
  const label = proved ? 'Passed' : 'Not tested';
  return (
    <div className={`rounded border p-4 ${toneClass(tone)}`} data-testid={`result-${title}`}>
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="mt-2 text-sm font-medium">{duration && proved ? `${label} — ${duration}` : label}</p>
      {historical ? <p className="mt-1 text-sm">Recorded test. This is not current backup health.</p> : null}
      {historical && result?.recordedAt ? <p className="mt-1 text-sm">Recorded {formatWhen(result.recordedAt)}</p> : null}
      {historical && result?.note ? <p className="mt-1 text-sm">{result.note}</p> : null}
    </div>
  );
}

function originText(value) {
  if (value === 'manual') return 'Manual';
  if (value === 'scheduled') return 'Scheduled';
  return 'Unknown';
}

function recordedScheduleText(schedule) {
  if (!schedule) return 'Unknown';
  if (schedule.state === 'pending') return 'Pending at last record';
  return 'Unknown';
}

function CoveragePanel({ coverage }) {
  const auth = coverage?.auth;
  const storage = coverage?.storage;
  const alerts = coverage?.alerts;
  const cards = [
    ['Auth', auth],
    ['Storage', storage],
  ];
  return (
    <section className="mt-4" data-testid="coverage-panel" aria-label="Auth and Storage backups">
      <h2 className="text-lg font-semibold text-gray-900">Auth and Storage</h2>
      <p className="mt-1 text-sm text-gray-700">Live status is unavailable unless this service can read sanitized backup status. A recorded success is not current backup health.</p>
      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        {cards.map(([title, section]) => {
          const success = section?.lastRecordedSuccess;
          return (
            <div key={title} className="rounded border border-gray-200 bg-gray-50 p-4" data-testid={`coverage-${title.toLowerCase()}`}>
              <h3 className="text-base font-semibold text-gray-900">{title}</h3>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Indicator label="Live status" value="Unavailable" tone="unavailable" />
                <Indicator label="Recorded schedule" value={recordedScheduleText(section?.recordedSchedule)} tone="unavailable" />
              </div>
              <p className="mt-3 text-sm text-gray-800">
                Last recorded success: {success ? `${originText(success.origin)}, ${formatWhen(success.completedAt)}` : 'Unknown'}
                {success?.recordedAt ? ` (recorded ${formatWhen(success.recordedAt)})` : ''}
              </p>
              <p className="mt-1 text-sm text-gray-700">{section?.recordedSchedule?.detail || section?.detail || 'Live status is unavailable.'}</p>
            </div>
          );
        })}
      </div>
      <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-4" data-testid="coverage-alerts">
        <h3 className="text-base font-semibold text-gray-900">Alerts</h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Indicator label="Live alert status" value="Unavailable" tone="unavailable" />
          <Indicator label="Inbox delivery" value={alerts?.inboxDeliveryConfirmed === true ? 'Confirmed' : 'Not confirmed'} tone="unavailable" />
        </div>
        <p className="mt-3 text-sm text-gray-800">Last recorded check: {formatWhen(alerts?.lastRecordedCheckAt)}</p>
        <p className="mt-1 text-sm text-gray-700">{alerts?.detail || 'Live alert status is unavailable.'}</p>
      </div>
    </section>
  );
}

function onTabKeyDown(event, tab, setTab) {
  const current = TABS.findIndex((item) => item.id === tab);
  const actions = {
    ArrowRight: (current + 1) % TABS.length,
    ArrowLeft: (current - 1 + TABS.length) % TABS.length,
    Home: 0,
    End: TABS.length - 1,
  };
  if (!(event.key in actions)) return;
  event.preventDefault();
  const next = TABS[actions[event.key]];
  setTab(next.id);
  document.getElementById(`backup-tab-${next.id}`)?.focus();
}

const BackupRecoveryPage = () => {
  const { userClaims, userData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const role = userClaims?.role || userData?.role;
  const blocked = role !== ROLES.SUPERADMIN || readSupportedTenantSession() || readDealerAdminSession();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(!blocked);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('backups');
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = refresh ? `${API_ROUTES.ADMIN_BACKUP_RECOVERY}?refresh=1` : API_ROUTES.ADMIN_BACKUP_RECOVERY;
      const response = await authenticatedFetch(endpoint);
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setReport(null);
        setError(body.error || 'Backup status is unavailable.');
        return;
      }
      setReport(body.data || null);
    } catch {
      setReport(null);
      setError('Backup status is unavailable.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!blocked) load(false);
  }, [blocked, load]);

  useEffect(() => {
    if (location.hash !== '#recovery-instructions') return;
    setTab('recovery');
    setInstructionsOpen(true);
  }, [location.hash]);

  useEffect(() => {
    if (location.hash !== '#recovery-instructions' || tab !== 'recovery' || !instructionsOpen) return;
    const node = document.getElementById('recovery-instructions');
    if (!node) return;
    node.focus();
    if (typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'nearest' });
  }, [location.hash, tab, instructionsOpen, report]);

  if (blocked) {
    return (
      <MainLayout>
        <p className="text-sm text-gray-700">Backup & Recovery is available to a superadmin outside support and dealer sessions.</p>
      </MainLayout>
    );
  }

  const live = report?.live;
  const tests = report?.restoreTests;
  const coverage = report?.coverage || (report ? RECORDED_COVERAGE : null);
  const recovery = {
    authRecordRecovery: tests?.authRecordRecovery || (report ? RECORDED_RECOVERY.authRecordRecovery : null),
    storageSoftDelete: tests?.storageSoftDelete || (report ? RECORDED_RECOVERY.storageSoftDelete : null),
    storageSnapshotRestore: tests?.storageSnapshotRestore || (report ? RECORDED_RECOVERY.storageSnapshotRestore : null),
  };
  const remaining = report?.remaining;
  const summary = error || !live ? {
    tone: 'unavailable',
    title: 'Status unavailable',
    detail: error || 'Backup status could not be read.',
  } : summarizeProtection(live);
  const schedulesKnown = Array.isArray(live?.schedules?.items);
  const daily = scheduleLabel(scheduleOf(live, 'daily'), schedulesKnown);
  const weekly = scheduleLabel(scheduleOf(live, 'weekly'), schedulesKnown);
  const pitr = enabledLabel(live?.database?.pointInTimeRecoveryEnabled);
  const deletion = enabledLabel(live?.database?.deleteProtectionEnabled);
  const restoreProved = tests?.backupRestore?.proved === true;

  function openInstructions(event) {
    event?.preventDefault();
    setTab('recovery');
    setInstructionsOpen(true);
    navigate({ hash: 'recovery-instructions' });
  }

  return (
    <MainLayout scrollMain>
      <div className="flex w-full max-w-none flex-col gap-4 px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Backup & Recovery</h1>
            <p className="mt-1 text-sm text-gray-600">Read-only status. This page cannot restore data or change a schedule.</p>
          </div>
          <Button type="button" variant="secondary" onClick={() => load(true)} disabled={loading} data-testid="backup-refresh">
            Refresh
          </Button>
        </div>

        <div role="tablist" aria-label="Backup and recovery" className="flex flex-wrap gap-2">
          {TABS.map((item) => {
            const selected = tab === item.id;
            return (
              <button
                key={item.id}
                id={`backup-tab-${item.id}`}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls={`backup-panel-${item.id}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setTab(item.id)}
                onKeyDown={(event) => onTabKeyDown(event, tab, setTab)}
                className={`rounded-md border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${selected ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-800'}`}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {loading && !report ? <LoadingSpinner text="Checking backup status..." /> : null}

        <div
          id="backup-panel-backups"
          role="tabpanel"
          aria-labelledby="backup-tab-backups"
          hidden={tab !== 'backups'}
        >
          <section aria-label="Backup summary" data-testid="backup-summary" className={`rounded border px-4 py-4 ${toneClass(summary.tone)}`}>
            <h2 className="text-xl font-semibold">{summary.title}</h2>
            <p className="mt-1 text-sm">{summary.detail}</p>
          </section>

          {live ? (
            <>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <Indicator label="Last successful backup" value={lastBackupText(live)} tone={schedulesKnown && live.schedules.items.some((item) => item.evidence === 'produced') ? 'protected' : (newestReady(live) || Array.isArray(live.backups?.items) ? 'pending' : 'unavailable')} />
                <Indicator label="Daily backup" value={daily.text} tone={daily.tone} />
                <Indicator label="Weekly backup" value={weekly.text} tone={weekly.tone} />
                <Indicator label="Point-in-time recovery" value={pitr.text} tone={pitr.tone} />
                <Indicator label="Delete protection" value={deletion.text} tone={deletion.tone} />
                <Indicator
                  label="Last checked"
                  value={live.freshness?.stale ? `${formatWhen(live.checkedAt)} (stale)` : formatWhen(live.checkedAt)}
                  tone={live.freshness?.stale ? 'unavailable' : 'pending'}
                />
              </div>
              <CoveragePanel coverage={coverage} />
              <div className="mt-4">
                <TechnicalDetails live={live} />
              </div>
            </>
          ) : null}
          {error ? <p className="mt-3 text-sm text-gray-700" data-testid="backup-load-error">{error}</p> : null}
        </div>

        <div
          id="backup-panel-recovery"
          role="tabpanel"
          aria-labelledby="backup-tab-recovery"
          hidden={tab !== 'recovery'}
        >
          <section className={`rounded border px-4 py-4 ${restoreProved ? toneClass('passed') : toneClass('pending')}`} data-testid="recovery-summary">
            <h2 className="text-xl font-semibold">
              {restoreProved ? 'Firestore restore tested successfully' : 'Firestore restore has not been recorded as tested'}
            </h2>
            <p className="mt-1 text-sm">Test date: {formatWhen(tests?.recordedAt)}</p>
          </section>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <ResultCard title="Backup restore" result={tests?.backupRestore} />
            <ResultCard title="Point-in-time recovery" result={tests?.pitrClone} />
            {recovery.authRecordRecovery ? <ResultCard title="Auth record recovery" result={recovery.authRecordRecovery} historical /> : null}
            {recovery.storageSoftDelete ? <ResultCard title="Storage soft-delete" result={recovery.storageSoftDelete} historical /> : null}
            {recovery.storageSnapshotRestore ? <ResultCard title="Storage snapshot restore" result={recovery.storageSnapshotRestore} historical /> : null}
          </div>
          {remaining?.cutoverRehearsed === true && remaining?.stagedRestartRehearsed === true && tests?.fullDisasterRecovery === true ? null : (
            <p className="mt-4 rounded border border-gray-200 bg-white px-4 py-3 text-sm text-gray-800" data-testid="recovery-untested-note">
              Full application recovery has not yet been rehearsed. Production cutover and staged restart remain untested.
            </p>
          )}
          <div className="mt-4 space-y-3">
            <details className="rounded border border-gray-200 bg-white">
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-900">Detailed test evidence</summary>
              <div className="space-y-2 border-t border-gray-200 px-4 py-3 text-sm text-gray-700">
                <p>Recorded {formatWhen(tests?.recordedAt)}. Source: {tests?.source || 'Unknown'}. Refresh does not change this record.</p>
                <p>Backup restore snapshot: {formatWhen(tests?.backupRestore?.snapshotTime)}. Finished: {formatWhen(tests?.backupRestore?.finishedAt)}. Expires: {formatWhen(tests?.backupRestore?.expireTime)}.</p>
                <p>{tests?.backupRestore?.note}</p>
                <p>Point-in-time recovery point: {formatWhen(tests?.pitrClone?.recoveryPoint)}. Finished: {formatWhen(tests?.pitrClone?.finishedAt)}.</p>
                <p>{tests?.pitrClone?.note}</p>
              </div>
            </details>
            <details className="rounded border border-gray-200 bg-white">
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-900">Remaining recovery work</summary>
              <ul className="list-disc space-y-1 border-t border-gray-200 px-8 py-3 text-sm text-gray-800">
                {(remaining?.items || []).map((item) => <li key={item}>{item}</li>)}
              </ul>
            </details>
            <details
              id="recovery-instructions"
              tabIndex={-1}
              open={instructionsOpen}
              onToggle={(event) => setInstructionsOpen(event.currentTarget.open)}
              className="rounded border border-gray-200 bg-white"
            >
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-900">Recovery instructions</summary>
              <div className="border-t border-gray-200 px-4 py-3">
                <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-800" data-testid="recovery-instructions">
                  {(remaining?.instructions || []).map((item) => <li key={item}>{item}</li>)}
                </ol>
                <a className="mt-3 inline-block text-sm text-blue-700 underline" href="#recovery-instructions" onClick={openInstructions}>Open recovery instructions</a>
              </div>
            </details>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default BackupRecoveryPage;
